import React, { useEffect, useState } from "react";
import Header from "../components/Admin/header/Header";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  Grid2,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  OutlinedInput,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getDynamicTemplateApi } from "../services/dynamic-template.service";
import { useGetApi } from "../hooks/useGetApi";
import { toast } from "react-toastify";
import {
  getPathologyByIdApi,
  updatePathologyApi,
} from "../services/pathology.service";

export default function PathologyAddReport() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const status = location.state?.status;
  const ID = location.state?.ID;
  const allDetails = location.state?.allData;
  const [nextLoading, setNextLoading] = useState(false);

  // Debug logs for dropdowns
  console.log('PathologyAddReport: render');

  const { dataList: results } = useGetApi({
    apiFunction: getPathologyByIdApi,
    params: { limit: Number.MAX_SAFE_INTEGER, id: ID },
  });

  const { dataList: allData } = useGetApi({
    apiFunction: getDynamicTemplateApi,
    params: { limit: Number.MAX_SAFE_INTEGER, item: id },
  });

  // Add state for machine and user dropdowns
  const [machineList, setMachineList] = useState([]);
  const [userList, setUserList] = useState([]);

  const [formData, setFormData] = useState([]);

  const sourceData =
    status === "Dr Approval"
      ? results?.report?.results
      : allData?.templates?.[0]?.dynamicForm;

  const handleInputChange = (id, value) => {
    setFormData((prevData) => {
      return prevData.map((item) =>
        item.id === id ? { ...item, value } : item
      );
    });
  };

  const handleCheckboxChange = (id) => {
    setFormData((prevData) =>
      prevData.map((item) =>
        item.id === id ? { ...item, isRequired: !item.isRequired } : item
      )
    );
  };

  const submitHandler = async () => {
    let isValid = true;

    formData.forEach((item) => {
      const row = allData?.templates?.[0]?.dynamicForm?.find(
        (row) => row._id === item.id
      );

      // Only validate if type is Numeric
      if (row?.fields?.[0]?.type === "Numeric") {
        const range = row?.fields?.[0]?.settings?.ranges?.[0];

        if (range === "-") return;

        let numericValue = parseFloat(item.value);
        let errorMessage = "";

        // Validation based on operation type
        if (range.operation === "Between") {
          if (numericValue < range.value1 || numericValue > range.value2) {
            errorMessage = `${row?.fields?.[0]?.displayText}: Value must be between ${range.value1} and ${range.value2}`;
          }
        } else if (range.operation === "Greater Than") {
          if (numericValue <= range.value1) {
            errorMessage = `${row?.fields?.[0]?.displayText}: Value must be greater than ${range.value1}`;
          }
        } else if (range.operation === "Less Than") {
          if (numericValue >= range.value1) {
            errorMessage = `${row?.fields?.[0]?.displayText}: Value must be less than ${range.value1}`;
          }
        }

        if (errorMessage) {
          toast.error(errorMessage);
          isValid = false;
        }
      }
    });

    if (isValid) {
      let response;

      if (formData?.some((item) => !item.value)) {
        toast.error("All fields are required.");
        return;
      }

      const payload = {
        status: status === "Dr Approval" ? "Dispatched" : "Dr Approval",
        report: {
          template: allData?.templates?.[0]?._id,
          results: formData.map(({ id, ...rest }) => rest),
        },
      };

      setNextLoading(true);
      response = await updatePathologyApi(ID, payload);
      setNextLoading(false);

      if (response.code === 200 || response.code === 201) {
        toast.success(`Pathology updated successfully`);
        navigate(-1);
      } else {
        toast.error(response?.message || "Some error occurred.");
      }
    }
  };

  useEffect(() => {
    // Log allDetails and allData for debugging
    console.log('allDetails:', allDetails);
    console.log('allData:', allData);
    // TODO: Add API calls to fetch machineList and userList here

    const sourceData =
      status === "Dr Approval"
        ? results?.report?.results
        : allData?.templates?.[0]?.dynamicForm;

    if (sourceData) {
      setFormData(
        sourceData.map((row) => ({
          id: row._id,
          label: row.label || row?.displayText,
          value: row.value || "",
          range:
            row.range ||
            (row?.fields?.[0]?.type === "Numeric"
              ? row?.fields?.[0]?.settings?.ranges?.[0]?.operation === "Between"
                ? `${row?.fields?.[0]?.settings?.ranges?.[0]?.value1} - ${row?.fields?.[0]?.settings?.ranges?.[0]?.value2}`
                : row?.fields?.[0]?.settings?.ranges?.[0]?.operation ===
                  "Greater Than"
                ? `> ${row?.fields?.[0]?.settings?.ranges?.[0]?.value1}`
                : row?.fields?.[0]?.settings?.ranges?.[0]?.operation ===
                  "Less Than"
                ? `< ${row?.fields?.[0]?.settings?.ranges?.[0]?.value1}`
                : "-"
              : "-"),
          unit: row.unit || row?.fields?.[0]?.unit || "",
          isRequired: row.isRequired ?? false,
        }))
      );
    }
  }, [allData, status]);

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box sx={{}}>
          <Sidebar />
        </Box>
        <Box sx={{ width: "100%" }}>
          {/*Pathology add report details */}
          <Tooltip title="Back to previous page" onClick={() => navigate(-1)}>
            <IconButton
              sx={{ paddingTop: 2, paddingBottom: 2, marginLeft: -2 }}
            >
              <KeyboardArrowLeftIcon />
              <Typography>Back</Typography>
            </IconButton>
          </Tooltip>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              marginRight: "90px",
            }}
          >
            <Typography
              variant="subtitle1"
              color="#5D6A82"
              paddingTop={2}
              paddingBottom={2}
            >
              Add Result Details
            </Typography>

            {/* right end close button  */}
            <Tooltip title="close">
              <IconButton>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {/* basic information box */}
          <Box
            sx={{
              // paddingTop: "20px",
              // width: "100%",
              marginBottom: "20px",
              marginRight: "90px",
              backgroundColor: "#F0F7FC",
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              paddingInline: "15px",
            }}
          >
            <Typography
              sx={{
                fontSize: 14,
                paddingLeft: 2,
                paddingTop: 2,
                paddingBottom: 2,
              }}
            >
              {" "}
              Basic Information
            </Typography>
            <Grid
              sx={{
                display: "flex",
                justifyContent: "space-evenly",
                paddingBottom: "20px",
              }}
              container
              spacing={2}
            >
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="Sample ID"
                  value={allDetails?.sample?.sampleId || ""}
                  sx={{ backgroundColor: "white" }}
                />
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <InputLabel id="demo-multiple-name-label">
                    Machine Name
                  </InputLabel>
                  <Select
                    sx={{ backgroundColor: "white" }}
                    labelId="demo-multiple-name-label"
                    id="demo-multiple-name"
                    input={<OutlinedInput label="Machine Name" />}
                  >
                    {/* <MenuItem value="dummy1">dummy1</MenuItem>
                    <MenuItem value="dummy2">dummy2</MenuItem> */}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <InputLabel id="demo-multiple-name-label">
                    Fetched By
                  </InputLabel>
                  <Select
                    sx={{ backgroundColor: "white" }}
                    labelId="demo-multiple-name-label"
                    id="demo-multiple-name"
                    input={<OutlinedInput label="Fetched By" />}
                  >
                    {/* <MenuItem value="dummy1">dummy1</MenuItem>
                    <MenuItem value="dummy2">dummy2</MenuItem> */}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          {/* checkBox */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              marginLeft: -1.5,
              marginBottom: 2,
            }}
          >
            <Checkbox />
            <Typography>Select All Report</Typography>
          </Box>

          {/* detailed report form  */}
          <Box
            sx={{
              // paddingTop: "20px",
              // width: "100%",
              marginBottom: "20px",
              marginRight: "90px",
              backgroundColor: "#F0F7FC",
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              paddingInline: "15px",
            }}
          >
            <Typography
              variant="subtitle1"
              color="#5D6A82"
              paddingTop={2}
              paddingBottom={2}
            >
              {allData?.templates?.[0]?.name}
            </Typography>
            {/* {allData?.templates?.[0]?.dynamicForm?.map((row) => (
              <Grid
                key={row?._id}
                container
                spacing={3}
                sx={{ paddingLeft: 2, paddingBottom: 2 }}
              >
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    type={
                      row?.fields?.[0]?.unit === "Numeric" ? "number" : "text"
                    }
                    label={row?.displayText}
                    sx={{ backgroundColor: "white" }}
                  />
                </Grid>
                <Grid
                  item
                  xs={1}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <Typography align="">{row?.fields?.[0]?.unit}</Typography>
                </Grid>
                <Grid
                  item
                  xs={1}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <Typography>
                    {row?.fields?.[0]?.settings?.ranges?.[0]?.operation ===
                    "Between"
                      ? `${row?.fields?.[0]?.settings?.ranges?.[0]?.value1} - ${row?.fields?.[0]?.settings?.ranges?.[0]?.value2}`
                      : row?.fields?.[0]?.settings?.ranges?.[0]?.operation ===
                        "Greater Than"
                      ? `> ${row?.fields?.[0]?.settings?.ranges?.[0]?.value1}`
                      : row?.fields?.[0]?.settings?.ranges?.[0]?.operation ===
                        "Less Than"
                      ? `< ${row?.fields?.[0]?.settings?.ranges?.[0]?.value1}`
                      : "N/A"}
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={2}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <Checkbox />
                  <Typography>Not Required</Typography>
                </Grid>
              </Grid>
            ))} */}
            {sourceData?.map((row) => {
              const currentItem =
                formData.find((item) => item.id === row._id) || {};
              const isDisabled = status === "Dr Approval";

              return (
                <Grid
                  key={row?._id}
                  container
                  spacing={3}
                  sx={{ paddingLeft: 2, paddingBottom: 2 }}
                >
                  <Grid item xs={4}>
                    {row?.fields?.[0]?.type === "Numeric" ||
                    status === "Dr Approval" ? (
                      <TextField
                        fullWidth
                        label={row.label || row?.fields?.[0]?.displayText}
                        sx={{ backgroundColor: "white" }}
                        value={currentItem.value || ""}
                        disabled={isDisabled}
                        onChange={(e) =>
                          handleInputChange(row._id, e.target.value)
                        }
                      />
                    ) : (
                      <FormControl variant="standard" fullWidth>
                        <InputLabel id="demo-simple-select-standard-label">
                          {row?.fields?.[0]?.displayText}
                        </InputLabel>
                        <Select
                          labelId="demo-simple-select-standard-label"
                          id="demo-simple-select-standard"
                          value={currentItem.value || ""}
                          disabled={isDisabled}
                          onChange={(e) =>
                            handleInputChange(row._id, e.target.value)
                          }
                          label={row?.fields?.[0]?.displayText}
                        >
                          {row?.fields?.[0]?.settings?.shortcuts?.map(
                            (shortcut, index) => (
                              <MenuItem key={index} value={shortcut?.shortForm}>
                                {shortcut?.shortForm}
                              </MenuItem>
                            )
                          )}
                        </Select>
                      </FormControl>
                    )}
                  </Grid>

                  <Grid
                    item
                    xs={1}
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <Typography>{currentItem.unit}</Typography>
                  </Grid>

                  <Grid
                    item
                    xs={1}
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <Typography>{currentItem.range || "N/A"}</Typography>
                  </Grid>

                  <Grid
                    item
                    xs={2}
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <Checkbox
                      checked={currentItem.isRequired || false}
                      disabled={isDisabled}
                      onChange={() => handleCheckboxChange(row._id)}
                    />
                    <Typography>Not Required</Typography>
                  </Grid>
                </Grid>
              );
            })}
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              marginBottom: 6,
              gap: 2,
            }}
          >
            {status === "Dr Approval" && (
              <Button variant="outlined" sx={{ width: "200px" }}>
                Print Preview
              </Button>
            )}
            <Button
              variant="contained"
              sx={{ width: "200px" }}
              onClick={submitHandler}
              disabled={nextLoading}
            >
              {nextLoading ? <CircularProgress size={"20px"} /> : "Save"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Container>
  );
}
